export interface RGB {
  r: number;
  g: number;
  b: number;
}

export interface ColorStop {
  value: number;
  color: string; // Hex color code, e.g. "#ff0000"
}

function hexToRgb(hex: string): RGB {
  const int = parseInt(hex.slice(1), 16);
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
}

/**
 * Pick a readable foreground color (black or white) for a given background hex.
 */
export function getContrastColor(hex: string): string {
  const { r, g, b } = hexToRgb(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#000000' : '#ffffff';
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

function interpolateRgb(c1: RGB, c2: RGB, t: number): RGB {
  return {
    r: Math.round(c1.r + (c2.r - c1.r) * t),
    g: Math.round(c1.g + (c2.g - c1.g) * t),
    b: Math.round(c1.b + (c2.b - c1.b) * t),
  };
}

export function getColor(value: number, colorStops: ColorStop[]): string {
  if (!colorStops || colorStops.length < 2) {
    throw new Error('At least two color stops are required.');
  }

  // Sort color stops by value
  colorStops = colorStops.slice().sort((a, b) => a.value - b.value);

  // Clamp value to the range
  const min = colorStops[0].value;
  const max = colorStops[colorStops.length - 1].value;
  if (value <= min) return colorStops[0].color;
  if (value >= max) return colorStops[colorStops.length - 1].color;

  // Find the two stops that value falls between
  let lower, upper;
  for (let i = 0; i < colorStops.length - 1; i++) {
    if (value >= colorStops[i].value && value <= colorStops[i + 1].value) {
      lower = colorStops[i];
      upper = colorStops[i + 1];
      break;
    }
  }

  const t = (value - lower.value) / (upper.value - lower.value);

  const lowerRgb = hexToRgb(lower.color);
  const upperRgb = hexToRgb(upper.color);

  const blended = interpolateRgb(lowerRgb, upperRgb, t);
  return rgbToHex(blended.r, blended.g, blended.b);
}
